"use client";

import { Check } from "lucide-react";
import { useAppDispatch } from "@/_hooks/hooks";
import { setStep } from "@/store/cartSlice";

const steps = [
  { id: 0, name: "Cart Details", shortName: "Cart" },
  { id: 1, name: "Payment", shortName: "Payment" },
];

export default function CartStepper({ currentStep }: { currentStep: number }) {
  const dispatch = useAppDispatch();

  const handleStepClick = (stepId: number) => {
    // Only allow going backward, not forward
    if (stepId < currentStep) {
      window.scrollTo(0, 0);
      dispatch(setStep(stepId));
    }
  };

  return (
    <nav aria-label="Progress" className="mb-6 md:mb-10 px-4">
      <ol
        role="list"
        className="flex items-center justify-center max-w-3xl mx-auto"
      >
        {steps.map((step, stepIdx) => (
          <li
            key={step.name}
            className={`flex items-center ${stepIdx !== steps.length - 1 ? "flex-1" : ""}`}
          >
            <button
              onClick={() => handleStepClick(step.id)}
              disabled={step.id > currentStep}
              className={`flex flex-col md:flex-row items-center group outline-none focus:ring-0 ${step.id < currentStep ? "cursor-pointer" : "cursor-default"}`}
            >
              <div className="flex items-center">
                {step.id < currentStep ? (
                  <span className="relative flex h-7 w-7 md:h-8 md:w-8 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-600 shadow-md transition-all">
                    <Check
                      className="h-3.5 w-3.5 md:h-4 md:w-4 text-white"
                      aria-hidden="true"
                    />
                  </span>
                ) : step.id === currentStep ? (
                  <span className="relative flex h-7 w-7 md:h-8 md:w-8 items-center justify-center rounded-full border-2 border-orange-500 bg-white shadow-sm">
                    <span className="h-2 w-2 md:h-2.5 md:w-2.5 rounded-full bg-orange-500" />
                  </span>
                ) : (
                  <span className="relative flex h-7 w-7 md:h-8 md:w-8 items-center justify-center rounded-full border border-gray-200 bg-white">
                    <span className="text-[10px] md:text-[11px] font-bold text-gray-400">
                      {step.id + 1}
                    </span>
                  </span>
                )}

                <span
                  className={`ml-2 md:ml-3 text-[10px] md:text-[13px] font-bold tracking-tight whitespace-nowrap
                  ${step.id < currentStep ? "text-gray-900" : step.id === currentStep ? "text-orange-600" : "text-gray-400"}
                `}
                >
                  <span className="hidden sm:inline">{step.name}</span>
                  <span className="sm:hidden">{step.shortName}</span>
                </span>
              </div>
            </button>

            {stepIdx !== steps.length - 1 && (
              <div className="flex-1 mx-2 md:mx-6">
                <div
                  className={`h-[1px] w-full transition-colors duration-500 ${step.id < currentStep ? "bg-orange-500" : "bg-gray-200"}`}
                />
              </div>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
